// MobileTopBar.tsx — the <1024px top bar (handoff README §1 "Mobile (<1024px)" table: "52px,
// --rail, bottom border. Hamburger 36px · status dot + current bot name 14px/600 (truncating) ·
// 30px avatar right"). Visible only below the rail's own breakpoint (`lg:hidden`); the rail
// itself already hides above 1024px via `hidden lg:flex` (Rail.tsx).
//
// Fix round 1 (code review): the avatar now opens the account menu directly
// (`WorkspaceMenuContent`, the same merged workspace+account dropdown `WorkspaceMenuRow` opens
// on desktop/from inside the drawer) — it does NOT open the navigation drawer. It previously
// did, under an `aria-label="Open account menu"` that lied
// about that: a screen-reader user acting on that label landed in a navigation list, and a
// sighted user got two controls doing the identical thing on one 52px bar. The hamburger is the
// navigation affordance; the avatar is the account affordance — checked against
// `Dashboard Shell.dc.html`'s mobile viewport, which renders the avatar as a plain, unwired
// `<span>` (no `onClick` at all, unlike the hamburger/close buttons, which both have one) — i.e.
// the reference mock doesn't wire *any* behavior into it, most likely because the .dc.html file
// is, per its own README, "a design reference, not production code" that never got this one
// interaction plumbed through. Wiring it to open the account menu (rather than leaving it
// inert-but-button-shaped, which would be its own accessibility trap) is the more useful and
// more honest reading of "the avatar is the account affordance" — a real dropdown, not a second
// drawer trigger and not a decorative dead click target.
import { forwardRef } from 'react';
import { MenuIcon } from 'lucide-react';
import { useParams } from 'react-router';
import { DropdownMenu, DropdownMenuTrigger } from '@repo/ui/components/dropdown-menu';
import { Skeleton } from '@repo/ui/components/skeleton';
import type { useSession } from '../../lib/auth-client';
import { useBots } from '../../hooks/useBots';
import { useT } from '../../locales/context';
import { RAIL_BG } from './Rail';
import { WorkspaceMenuContent } from './WorkspaceMenu';

type Session = NonNullable<ReturnType<typeof useSession>['data']>;

const BAR_SHELL = `flex h-[52px] shrink-0 items-center gap-2.5 border-b border-border px-3 lg:hidden ${RAIL_BG}`;

/** Shown while `useBots` is in flight (session already resolved by `Protected`, bots not yet
 * loaded) — same "one shape per real zone" rule as `RailSkeleton`, never a bare loading label. */
export function MobileTopBarSkeleton() {
  return (
    <div className={BAR_SHELL} aria-hidden="true">
      <Skeleton className="size-9 shrink-0 rounded-lg" />
      <Skeleton className="h-[18px] w-32 flex-1" />
      <Skeleton className="size-[30px] shrink-0 rounded-full" />
    </div>
  );
}

export const MobileTopBar = forwardRef<
  HTMLButtonElement,
  { session: Session; drawerOpen: boolean; onOpenMenu: () => void; onSignOut: () => void }
>(function MobileTopBar({ session, drawerOpen, onOpenMenu, onSignOut }, hamburgerRef) {
  const { botId: routeBotId } = useParams();
  const { data: bots, isPending, error } = useBots();
  const t = useT();

  if (isPending) return <MobileTopBarSkeleton />;

  const currentBot = bots?.find((b) => b.id === routeBotId) ?? bots?.[0];
  const initial = session.user.email.charAt(0).toUpperCase();
  const isAdmin = session.user.role === 'admin';

  // Fix-wave item 1 — same shape as `Rail.tsx`'s `RailError`: a settled-but-failed `useBots()`
  // must not blank out this bar's two navigation affordances. The hamburger still opens
  // `MobileDrawer` (which renders its own error state below) and the avatar still opens the
  // account menu directly — sign-out never depends on the bots list loading.
  return (
    <div className={BAR_SHELL} role={error ? 'alert' : undefined}>
      <button
        ref={hamburgerRef}
        type="button"
        aria-label={t.shell.openNavigation}
        aria-haspopup="dialog"
        aria-expanded={drawerOpen}
        aria-controls="mobile-nav-drawer"
        onClick={onOpenMenu}
        className="grid size-9 shrink-0 place-items-center rounded-lg text-foreground transition-colors hover:bg-accent focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35"
      >
        <MenuIcon size={18} strokeWidth={2} />
      </button>

      {error && (
        <span className="min-w-0 flex-1 truncate text-[13px] font-medium text-destructive">
          {t.shell.botsLoadErrorInline}
        </span>
      )}
      {!error && currentBot && (
        <div className="flex min-w-0 flex-1 items-center gap-[7px]">
          <span
            aria-hidden="true"
            className="size-[7px] shrink-0 rounded-full"
            style={{ backgroundColor: `var(${currentBot.status === 'active' ? '--st-ready' : '--st-paused'})` }}
          />
          <span className="truncate text-sm font-semibold text-foreground">{currentBot.name}</span>
        </div>
      )}
      {!error && !currentBot && <div className="flex-1" />}

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={t.shell.openAccountMenu}
            className="grid size-[30px] shrink-0 place-items-center rounded-full bg-primary text-[12.5px] font-semibold text-primary-foreground focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/35"
          >
            {initial}
          </button>
        </DropdownMenuTrigger>
        <WorkspaceMenuContent
          email={session.user.email}
          isAdmin={isAdmin}
          onSignOut={onSignOut}
          align="end"
          side="bottom"
        />
      </DropdownMenu>
    </div>
  );
});
